import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  notificationService,
  type DashboardBanner,
} from "../../services/notificationService";

// Retention banners at the top of the dashboard (served by getDashboardBanners).
const DashboardBanners: React.FC = () => {
  const navigate = useNavigate();
  const [banners, setBanners] = useState<DashboardBanner[]>(() =>
    notificationService.getCachedDashboardBanners(),
  );

  useEffect(() => {
    let cancelled = false;
    const fetchBanners = () => {
      notificationService.getDashboardBanners().then((next) => {
        if (!cancelled) setBanners(next);
      });
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) fetchBanners();
    };

    fetchBanners();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const handleClose = (bannerId: string) => {
    setBanners((prev) => prev.filter((banner) => banner.id !== bannerId));
    notificationService.closeDashboardBanner(bannerId);
  };

  if (banners.length === 0) return null;

  return (
    <div className="mx-auto max-w-3xl">
      {banners.map((banner) => (
        <div
          key={banner.id}
          className="relative bg-base-100 rounded-none md:rounded-lg px-8 py-4 mt-3"
        >
          <button
            type="button"
            onClick={() => handleClose(banner.id)}
            aria-label="Close"
            className="btn btn-ghost btn-square btn-sm min-h-11 h-11 w-11 rounded-xl text-base-content/70 hover:text-base-content absolute top-1 right-1"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
          <div className="pr-8">
            <p className="font-bold text-base-content font-heading">
              {banner.title}
            </p>
            <p className="text-sm text-base-content/70 font-grotesk mt-1">
              {banner.body}
            </p>
          </div>
          {banner.buttons.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {banner.buttons.map((button, index) => (
                <button
                  key={`${button.label}-${index}`}
                  type="button"
                  onClick={() => navigate(button.url)}
                  className={
                    index === 0
                      ? "btn btn-primary btn-sm min-h-11 h-11 rounded-xl px-3 text-xs font-medium font-grotesk normal-case"
                      : "btn btn-ghost btn-sm min-h-11 h-11 rounded-xl px-3 text-xs font-medium border border-base-300 text-base-content/70 bg-base-100 font-grotesk normal-case hover:border-base-content/40 hover:bg-base-100"
                  }
                >
                  {button.label}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default DashboardBanners;
